import { useEffect, useRef } from "react";
import { Crosshair } from "lucide-react";
import { heatmapWave } from "@/lib/motion";

const C = { surface: "var(--bc-panel, #09090B)", border: "var(--bc-border, rgba(255,255,255,0.07))", muted: "var(--bc-sub, #71717A)", purple: "#A78BFA" };

function probToColor(prob, maxProb) {
  if (prob <= 0) return "var(--bc-panel-elev, rgba(255,255,255,0.03))";
  const r = Math.min(1, prob / Math.max(maxProb, 0.1));
  const alpha = 0.12 + r * 0.78;
  const red   = Math.round(40 + r * 127);
  const green = Math.round(10 + r * 59);
  const blue  = Math.round(60 + r * 190);
  return `rgba(${red},${green},${blue},${alpha})`;
}

export default function ScoreHeatmap({ heatmap, homeTeam, awayTeam }) {
  const gridRef = useRef(null);
  useEffect(() => {
    if (!gridRef.current) return;
    const cells = gridRef.current.querySelectorAll("[data-testid^='heatmap-cell-']");
    if (!cells.length) return;
    heatmapWave(cells, { gridCols: 5, gridRows: 5, color: "rgba(167,139,250,0.65)" });
  }, [heatmap]);

  if (!heatmap || heatmap.length === 0) return null;

  const lookup = {};
  let maxProb = 0;
  heatmap.forEach(({ h, a, prob }) => {
    lookup[`${h}_${a}`] = prob;
    if (prob > maxProb) maxProb = prob;
  });

  const topCell = heatmap.reduce(
    (best, cur) => cur.prob > best.prob ? cur : best,
    { h: 0, a: 0, prob: 0 }
  );

  const homeLabel = (homeTeam || "Casa").slice(0, 8);
  const awayLabel = (awayTeam || "Ospite").slice(0, 8);

  return (
    <div data-testid="score-heatmap"
      style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: `2px solid ${C.purple}` }}>
      <div className="px-4 pt-3 pb-3">

        {/* Header — stessa struttura di GundamScore */}
        <div className="flex items-center gap-2 mb-3">
          <Crosshair size={14} style={{ color: C.purple }} />
          <span className="font-heading font-black text-xs uppercase tracking-[0.2em]" style={{ color: C.purple }}>
            HEATMAP 5×5
          </span>
          <span className="font-data text-[10px] uppercase tracking-widest" style={{ color: C.muted }}>
            — Risultati Esatti
          </span>
        </div>

        {/* Grid compatta */}
        <div className="flex gap-1 items-start">

          {/* Asse Y (squadra casa) — solo iniziale + freccia */}
          <div className="flex flex-col items-center" style={{ gap: 2, paddingTop: 14 }}>
            {[0, 1, 2, 3, 4].map(h => (
              <div key={h}
                className="flex items-center justify-center font-data tabular-nums"
                style={{ width: 14, height: 28, fontSize: "0.6rem", color: C.muted, flexShrink: 0 }}>
                {h}
              </div>
            ))}
            <div className="font-data text-center mt-0.5"
              style={{ fontSize: "0.55rem", color: C.muted, lineHeight: 1.1, maxWidth: 14 }}>
              {homeLabel.slice(0, 3)}
            </div>
          </div>

          {/* Colonne */}
          <div className="flex-1" ref={gridRef}>
            {/* Header colonne (away goals) */}
            <div className="flex mb-1" style={{ gap: 2 }}>
              {[0, 1, 2, 3, 4].map(a => (
                <div key={a} className="flex items-center justify-center font-data tabular-nums"
                  style={{ flex: 1, fontSize: "0.6rem", color: C.muted, height: 14 }}>
                  {a}
                </div>
              ))}
            </div>

            {/* Righe della griglia */}
            {[0, 1, 2, 3, 4].map(h => (
              <div key={h} className="flex" style={{ gap: 2, marginBottom: 2 }}>
                {[0, 1, 2, 3, 4].map(a => {
                  const prob = lookup[`${h}_${a}`] || 0;
                  const isTop = topCell.h === h && topCell.a === a;
                  const bg = probToColor(prob, maxProb);
                  return (
                    <div key={a}
                      data-testid={`heatmap-cell-${h}-${a}`}
                      className="flex items-center justify-center"
                      style={{
                        flex: 1,
                        height: 28,
                        background: bg,
                        border: isTop
                          ? `1.5px solid ${C.purple}`
                          : "1px solid var(--bc-panel-elev, rgba(255,255,255,0.04))",
                        boxShadow: isTop ? `0 0 6px rgba(167,139,250,0.35)` : "none",
                      }}>
                      <span style={{
                        fontSize: "0.55rem",
                        color: prob >= 6 ? "#fff" : "rgba(255,255,255,0.35)",
                        fontWeight: isTop ? 700 : 400,
                        fontVariantNumeric: "tabular-nums",
                      }}>
                        {prob > 0 ? `${prob}%` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Label squadra ospite sotto le colonne */}
            <div className="flex justify-center mt-1">
              <span style={{ fontSize: "0.55rem", color: C.muted }}>{awayLabel}</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
